import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@app/layouts/AppLayout';
import { AuthLayout } from '@app/layouts/AuthLayout';
import { LocalizedRoutes } from '@app/routes/LocalizedRoutes';
import { ProtectedRoute, PublicRoute } from '@/routes/ProtectedRoute';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@app/pages/NotFoundPage'));
const AuthSessionsPage = lazy(() => import('@features/auth/pages/AuthSessionsPage'));
const FuelReceptionListPage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionListPage')
);
const FuelReceptionCreatePage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionCreatePage')
);
const FuelReceptionDetailPage = lazy(
  () => import('@features/fuelReception/pages/FuelReceptionDetailPage')
);
const OfficialReportsPage = lazy(() => import('@features/reports/pages/OfficialReportsPage'));
const WellsConfigPage = lazy(() => import('@features/wellMonitoring/pages/WellsConfigPage'));
const WellFormPage = lazy(() => import('@features/wellMonitoring/pages/WellFormPage'));
const InspectionListPage = lazy(() => import('@features/wellMonitoring/pages/InspectionListPage'));
const InspectionCreatePage = lazy(
  () => import('@features/wellMonitoring/pages/InspectionCreatePage')
);
const InspectionDetailPage = lazy(
  () => import('@features/wellMonitoring/pages/InspectionDetailPage')
);
const StationAssetsPage = lazy(() => import('@features/stations/pages/StationAssetsPage'));
const StationAreaFormPage = lazy(() => import('@features/stations/pages/StationAreaFormPage'));
const StationComplianceProfilePage = lazy(
  () => import('@features/stations/pages/StationComplianceProfilePage')
);
const StationTankFormPage = lazy(() => import('@features/stations/pages/StationTankFormPage'));
const BillingPage = lazy(() => import('@features/billing/pages/BillingPage'));
const LegalDocumentPage = lazy(() => import('@features/legal/pages/LegalDocumentPage'));
const CalendarPage = lazy(() => import('@features/compliance/pages/CalendarPage'));
const LandingPage = lazy(() => import('@features/marketing/pages/LandingPage'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/es" replace />
  },
  {
    path: '/:lng',
    element: <LocalizedRoutes />,
    children: [
      // Public marketing landing at the localized root (e.g. /es). Sign-in is
      // offered in a modal here; the dedicated /login page stays as a deep link.
      { index: true, element: <LandingPage /> },
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: (
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            )
          }
        ]
      },
      // Public legal pages: reachable with or without a session (the sign-in
      // screen links here, and the privacy notice must be readable before any
      // data is collected). No auth wrapper, so they render their own layout.
      { path: 'legal/privacy', element: <LegalDocumentPage documentType="privacy_notice" /> },
      { path: 'legal/terms', element: <LegalDocumentPage documentType="terms" /> },
      {
        element: (
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'compliance/calendar', element: <CalendarPage /> },
          { path: 'fuel-receptions', element: <FuelReceptionListPage /> },
          { path: 'fuel-receptions/new', element: <FuelReceptionCreatePage /> },
          { path: 'fuel-receptions/:id', element: <FuelReceptionDetailPage /> },
          { path: 'reports/official', element: <OfficialReportsPage /> },
          { path: 'monitoring/inspections', element: <InspectionListPage /> },
          { path: 'monitoring/inspections/new', element: <InspectionCreatePage /> },
          { path: 'monitoring/inspections/:id', element: <InspectionDetailPage /> },
          { path: 'monitoring/wells', element: <WellsConfigPage /> },
          { path: 'monitoring/wells/new', element: <WellFormPage /> },
          { path: 'monitoring/wells/:wellId/edit', element: <WellFormPage /> },
          { path: 'settings', element: <Navigate to="dashboard" replace /> },
          { path: 'settings/sessions', element: <AuthSessionsPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'stations/:stationId/assets', element: <Navigate to="../areas" replace /> },
          { path: 'stations/:stationId/areas', element: <StationAssetsPage mode="areas" /> },
          { path: 'stations/:stationId/areas/new', element: <StationAreaFormPage /> },
          { path: 'stations/:stationId/areas/:areaId/edit', element: <StationAreaFormPage /> },
          { path: 'stations/:stationId/tanks', element: <StationAssetsPage mode="tanks" /> },
          { path: 'stations/:stationId/tanks/new', element: <StationTankFormPage /> },
          { path: 'stations/:stationId/tanks/:tankId/edit', element: <StationTankFormPage /> },
          {
            path: 'stations/:stationId/dispensers',
            element: <StationAssetsPage mode="dispensers" />
          },
          {
            path: 'stations/:stationId/compliance-profile',
            element: <StationComplianceProfilePage />
          }
        ]
      },
      { path: '*', element: <NotFoundPage /> }
    ]
  }
]);
